// MAJLIS-113 (UI-C33, UI-C34, D-54, P-34; PROPOSED): the voice isolator's compare mode, drawn ONLY on the compared
// seat's own phone, as the approved mock-up draws it (docs/spec/prototype/compare-mode-mockup.html): the header pill,
// the banner line, and under each of this seat's phrases the isolator's transcript of the same audio - red on finals
// only, the dashed "(nothing)" when it produced none, and a waiting line while the phrase is live. Every other phone
// never receives compare_mode, so none of this renders there.
import { dirOf, useI18n } from "../i18n";
import type { CompareView } from "../room/model";

/** UI-C33: the dashed pill in the header - "this phone is under test". Not a control.
 * MAJLIS-175 (the screen-rules mock-up, frame (o)): on a screen narrower than COMPARE_WORD_FROM_PX (360) it keeps only its
 * mark - the dot in a 32 x 32 px dashed ring - so the room's code and mode line stay whole; the word stays in the DOM for
 * a screen reader (app.css hides it visually, the .visually-hidden way). At 360 px and up it shows the word. */
export function ComparePill() {
  const { t } = useI18n();
  return (
    <span className="compare-pill" data-spec="UI-C33 D-54">
      <i aria-hidden="true" />
      <span className="compare-word">{t("compareLabel")}</span>
    </span>
  );
}

/** UI-C33: the banner line, in the one banner slot at its lowest rank (any other line outranks it while it shows).
 * MAJLIS-174: the slot's ONE plain style - no red box (red stays reserved for Leave and End). */
export function CompareBanner() {
  const { lang, t } = useI18n();
  return (
    <div className="engine-banner compare-banner" role="status" lang={lang} dir={dirOf(lang)} data-spec="UI-C33 D-54">
      {t("compareBanner")}
    </div>
  );
}

/** UI-C33/UI-C34: the slot under one of this seat's bubbles (the pairing and the absence rule: room/model.ts). */
export function CompareSlot({ view, bubbleId }: { view: CompareView; bubbleId: string }) {
  const { lang, t } = useI18n();
  if (view.kind === "pending") {
    return (
      <p className="compare-pending" data-compare-for={bubbleId} lang={lang} dir={dirOf(lang)} data-spec="UI-C34">
        {t("isolatorPending")}
      </p>
    );
  }
  const empty = view.kind === "absent";
  const text = empty ? null : view.phrases.map((p) => p.text).join(" ");
  const textLang = empty ? lang : view.phrases[0].lang;
  return (
    <div className="compare-slot" data-compare-for={bubbleId} data-spec={empty ? "UI-C34 EM-31" : "UI-C33 EM-31"}>
      <div className={empty ? "bubble-compare empty" : "bubble-compare"} dir={dirOf(lang)}>
        <p className="isolator-tag" lang={lang}>
          {t("isolatorTag")}
        </p>
        <p className="compare-text" lang={textLang} dir={dirOf(textLang)}>
          {empty ? t("isolatorEmpty") : text}
        </p>
      </div>
    </div>
  );
}
